import { Dispatch, SetStateAction } from "react";

// Shared input primitives for /beta-review (short, per-group), /beta-review/full
// (long, universal), and /beta-review/short (10-question, universal). Kept in
// one place so the scale/choice UI reads identically across all three — same
// tap targets, same anchor-label convention.

// Reused across every "what did you use it for" / "most valuable" /
// "confusing" feature question across all 3 survey variants, so answers stay
// cross-tabbable against one taxonomy instead of each form inventing its own.
export const FEATURE_OPTIONS = [
  "Browsing events",
  "Joining a loop",
  "Creating a loop",
  "Chatting with people",
  "Finding people nearby",
  "Just having a look around",
  "Something else",
];

// SEG1/USE1 display-label -> DB enum mappings, shared across all 3 forms so
// the segment/usage_amount columns stay filterable regardless of which form
// a response came from.
export function usageAmountToEnum(label?: string): string | null {
  switch (label) {
    case "Just the once":
      return "just_once";
    case "A handful of times":
      return "handful";
    case "Regularly":
      return "regularly";
    default:
      return null;
  }
}

export function segmentToEnum(label?: string): string | null {
  switch (label) {
    case "New to London":
      return "new_to_london";
    case "Have a friend group but want more to do":
      return "has_friend_group";
    case "Struggle to make plans happen":
      return "struggles_to_plan";
    case "Just curious":
      return "curious";
    default:
      return null;
  }
}

export function ScaleInput({
  value,
  onChange,
  lowLabel,
  highLabel,
  min = 1,
  max = 5,
}: {
  value: number | null;
  onChange: (n: number) => void;
  lowLabel: string;
  highLabel: string;
  // Only the long form's true 0-10 NPS question overrides these — every
  // other scale in the survey stays the standard labelled 1-5 (faster to tap,
  // internal comparability matters more than external benchmarking at this
  // sample size; see world-class-survey-review-2026-07-05.md).
  min?: number;
  max?: number;
}) {
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div>
      <div className="flex gap-2 flex-wrap">
        {points.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            className={
              "flex-1 min-w-[2.75rem] h-12 border-2 border-ink font-semibold text-lg transition-colors " +
              (value === n
                ? "bg-salmon text-on-salmon"
                : "bg-canvas text-ink hover:bg-paper-tan")
            }
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-600 mt-1.5 px-0.5">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}

export function ChoiceInput({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string | undefined;
  onChange: (opt: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          aria-pressed={value === opt}
          className={
            "flex-1 min-w-[5rem] min-h-[2.75rem] flex items-center justify-center text-center leading-snug px-3 py-2 border-2 border-ink font-medium transition-colors " +
            (value === opt
              ? "bg-salmon text-on-salmon"
              : "bg-canvas text-ink hover:bg-paper-tan")
          }
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function MultiChoiceInput({
  options,
  values,
  onChange,
}: {
  options: string[];
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (opt: string) => {
    onChange(
      values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt],
    );
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          aria-pressed={values.includes(opt)}
          className={
            "px-3.5 min-h-[2.75rem] flex items-center justify-center text-center leading-snug py-2 border-2 border-ink font-medium transition-colors " +
            (values.includes(opt)
              ? "bg-salmon text-on-salmon"
              : "bg-canvas text-ink hover:bg-paper-tan")
          }
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  rows = 2,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full border-2 border-ink p-3 focus:outline-none focus:ring-2 focus:ring-salmon resize-none"
    />
  );
}

export function Honeypot({
  value,
  setValue,
}: {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
}) {
  return (
    <input
      type="text"
      name="company"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      autoComplete="off"
      tabIndex={-1}
      aria-hidden="true"
      className="absolute -left-[9999px] w-px h-px opacity-0"
    />
  );
}

export type SurveySubmitPayload = {
  group: string;
  formVariant: "short" | "full" | "short10";
  testerName?: string;
  // Stable identifier read from the tester's link (?s=<slug>), submitted as-is
  // regardless of what the tester types into the editable name field — see
  // LL-0184 NORTH-STAR.md. Distinct from testerName, which stays free text.
  testerSlug?: string;
  contactEmail?: string;
  contactPhone?: string;
  easeScore?: number | null;
  satisfactionScore?: number | null;
  referralScore?: number | null;
  returnScore?: number | null;
  usageAmount?: string | null;
  segment?: string | null;
  answers: Record<string, string>;
  honeypot: string;
};

export async function submitSurvey(
  payload: SurveySubmitPayload,
): Promise<boolean> {
  const res = await fetch("/api/survey/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      group: payload.group,
      form_variant: payload.formVariant,
      tester_name: payload.testerName || undefined,
      tester_slug: payload.testerSlug || undefined,
      contact_email: payload.contactEmail || undefined,
      contact_phone: payload.contactPhone || undefined,
      ease_score: payload.easeScore ?? undefined,
      satisfaction_score: payload.satisfactionScore ?? undefined,
      referral_score: payload.referralScore ?? undefined,
      return_score: payload.returnScore ?? undefined,
      usage_amount: payload.usageAmount ?? undefined,
      segment: payload.segment ?? undefined,
      answers: payload.answers,
      company: payload.honeypot,
    }),
  });
  return res.ok;
}
